import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FeatureFlagService } from './feature-flag.service';
import { ANAESTHESIA_FIELD_LABELS } from '../common/anaesthesiaIntake';

// Anaesthesia (pilot) intake forms, keyed by specialtyIntakeData.specialtyType
const ANAESTHESIA_ARRAY_FIELDS: { [specialty: string]: string[] } = {
  'Anesthesia Planning Consult': [
    'anesthesiaMachineEquipment', 'monitoringEquipment', 'premedicationSedationDrugs',
    'inductionDrugs', 'reversalAgents', 'localRegionalAnesthesia', 'emergencyDrugs'
  ],
  'Pain Management Consult': []
};

const ANAESTHESIA_FIELD_MAX_LENGTHS: { [specialty: string]: { [field: string]: number } } = {
  'Anesthesia Planning Consult': {
    plannedProcedureType: 1000,
    procedureDate: 10,
    previousAnesthesia: 10,
    lastPhysicalExamDate: 10,
    asaStatus: 5,
    recentDiagnostics: 1000,
    currentMedications: 1000,
    temperatureMonitoringTypes: 500,
    mechanicalVentilator: 5,
    ventilatorMakeModel: 500,
    otherOpioids: 500,
    inductionOther: 500,
    reversalOther: 500,
    localRegionalOther: 500
  },
  'Pain Management Consult': {
    painLocation: 1000,
    primaryDiagnosis: 1000,
    primaryPainMechanism: 1000,
    currentPainScore: 1000,
    mobilityChange: 1000,
    appetiteChange: 1000,
    sleepingResting: 1000,
    currentAnalgesicTherapy: 2000,
    nonPharmacologicalTreatments: 1000,
    previousDrugReactions: 1000,
    organSystemConcerns: 1000,
    clientMedicationComfort: 1000
  }
};

export interface SpecialtyIntakeField {
  fieldName: string;
  label: string;
  value: any;
  type: string;
}

@Injectable({
  providedIn: 'root'
})
export class SpecialtyIntakeService {

  constructor(private featureFlagService: FeatureFlagService) { }

  /**
   * Generic method to check if specialty intake data exists and has content
   */
  hasSpecialtyIntakeData(appointmentDetails: any): boolean {
    const data = appointmentDetails?.specialtyIntakeData;
    if (!data || typeof data !== 'object') return false;

    // Check if any field has a non-empty value
    return Object.keys(data).some(key => {
      const value = data[key];
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(v => v && String(v).trim().length > 0);
      }
      return value !== null && value !== undefined && value !== '';
    });
  }

  /**
   * Generic method to add specialty intake fields to any form
   */
  addSpecialtyIntakeFieldsToForm(appointmentForm: any, specialtyData: any): SpecialtyIntakeField[] {
    const specialtyIntakeFields: SpecialtyIntakeField[] = [];
    const specialtyType = specialtyData?.specialtyType || '';

    if (specialtyData && typeof specialtyData === 'object') {
      Object.keys(specialtyData).forEach(fieldName => {
        // Skip adding the specialtyType field as it's metadata, not a form field
        if (fieldName !== 'specialtyType') {
          let fieldValue = specialtyData[fieldName] || '';

          // Transform modality value: 'chat' should display as 'Standard'
          if (fieldName.toLowerCase() === 'modality' && typeof fieldValue === 'string') {
            fieldValue = this.featureFlagService.getModalityDisplayName(fieldValue);
          }

          // Add the field to the form if it doesn't already exist
          if (!appointmentForm.get(fieldName)) {
            appointmentForm.addControl(fieldName, new FormControl(fieldValue));
          } else {
            // Update the existing field's value
            appointmentForm.get(fieldName).setValue(fieldValue);
          }

          // Create field metadata for display
          specialtyIntakeFields.push({
            fieldName: fieldName,
            label: this.getFieldLabel(fieldName, specialtyType),
            value: fieldValue,
            type: this.getFieldType(fieldName, specialtyType)
          });
        }
      });
    }

    return specialtyIntakeFields;
  }

  /**
   * Generic method to get user-friendly labels for fields
   * Now specialty-aware to prevent conflicts between specialties
   */
  getFieldLabel(fieldName: string, specialtyType?: string): string {
    const commonFieldLabels: { [field: string]: string } = {
      'prefersPhoneCall': 'Prefers Phone Call During Scheduled Appointment',
      'preferredPhoneNumber': 'Preferred Phone Number'
    };

    if (commonFieldLabels[fieldName]) {
      return commonFieldLabels[fieldName];
    }

    const fieldLabelsBySpecialty: { [specialty: string]: { [field: string]: string } } = {
      'Feline Behavior': {
        'familyComposition': 'Family Composition',
        'primaryBehaviorProblems': 'Primary Behavior Problems',
        'lifeEventsChanges': 'Life Events and Changes',
        'typicalDayAndProblems': 'Typical Day and Problems',
        'behaviorTreatmentsTrialed': 'Behavior Treatments Trialed',
        'medicationHistory': 'Medication History',
        'relationshipWithOtherCats': 'Relationship with Other Cats',
        'generalTemperament': 'General Temperament',
        'litterBoxDetails': 'Litter Box Details',
        'additionalInformation': 'Additional Information',
        // Recheck-specific fields
        'recheckUpdatedWeight': 'Updated Weight',
        'recheckCurrentBehaviorMedicalConditions': 'Current Behavior and Medical Conditions',
        'recheckCurrentMedicationsResponse': 'Current Medications and Response',
        'recheckGeneralProgressUpdate': 'General Progress Update',
        'recheckBehaviorManagementModification': 'Behavior Management Modification',
        'recheckSpecificGoalsQuestions': 'Specific Goals and Questions'
      },
      'Canine Behavior': {
        'dogFamilyComposition': 'Family Composition',
        'dogBehaviorProblems': 'Primary Behavior Problems',
        'dogLifeEventsChanges': 'Life Events and Changes',
        'dogTypicalDay': 'Typical Day',
        'aggressionTriggers': 'Aggression Triggers',
        'aggressionOtherSpecify': 'Other Aggression Triggers',
        'aggressionBehaviors': 'Aggression Behaviors',
        'bitingIncidents': 'Biting Incidents',
        'aggressionTargets': 'Aggression Targets',
        'fearfulNoiseResponse': 'Fearful/Noise Response',
        'separationAnxiety': 'Separation Anxiety',
        'behaviorTreatmentsAttempted': 'Behavior Treatments Attempted',
        'trainersWorkedWith': 'Trainers Worked With',
        'equipmentUsed': 'Equipment Used',
        'cuesSkillsWorkedOn': 'Cues/Skills Worked On',
        'dogMedicationHistory': 'Medication History',
        'dogAdditionalInformation': 'Additional Information',
        // Recheck-specific fields
        'recheckUpdatedWeight': 'Updated Weight',
        'recheckCurrentBehaviorMedicalConditions': 'Current Behavior and Medical Conditions',
        'recheckCurrentMedicationsResponse': 'Current Medications and Response',
        'recheckGeneralProgressUpdate': 'General Progress Update',
        'recheckBehaviorManagementModification': 'Behavior Management Modification',
        'recheckSpecificGoalsQuestions': 'Specific Goals and Questions'
      },
      'Recheck Oncology': {
        'updatedWeight': 'Updated Weight',
        'updatedWeightUnit': 'Weight Unit',
        'clinicalHistorySummary': 'Summary of Clinical History Since Previous Consultation',
        'updatedDiagnostics': 'Updated Diagnostics Performed',
        'otherPertinentHistory': 'Other Pertinent History/Information',
        'currentMedicationsTreatments': 'Current Medications and Treatments',
        'responseToPreviousMedications': 'Response to Previous Medications',
        'additionalDiagnostics': 'Additional Diagnostics or Information',
        'patientQualityOfLife': 'Patient Quality of Life (1-10 scale)',
        'specificQuestionsGoals': 'Specific Questions/Goals of Consultation',
        'newChemotherapyProtocol': 'New Chemotherapy Protocol Requested'
      },
      'Initial Oncology': {
        'patientDiagnosis': 'Patient Diagnosis',
        'clinicalHistorySummary': 'Summary of Clinical History',
        'diagnosticsPerformed': 'Diagnostics Performed',
        'previousCancers': 'Previous cancers diagnosed?',
        'currentMedicationsTreatments': 'Current medications and Treatments',
        'responseToPreviousMedications': 'Response to Previous Medications',
        'surgeryPerformed': 'Surgery Performed',
        'additionalDiagnostics': 'Additional diagnostics or information',
        'patientQualityOfLife': 'Patient Quality of Life (1-10 scale)',
        'specificQuestionsGoals': 'Specific Questions/Goals of Consultation',
        'chemotherapyProtocolRequested': 'Chemotherapy protocol requested?'
      },
      'Emergency and Critical Care': {
        'currentDiet': 'Current diet',
        'currentMedications': 'Current medications (if applicable)',
        'diagnosticsPerformed': 'Diagnostics performed',
        'consultationGoals': 'What are your goals for the consultation?',
        'priority': 'Priority Level',
        'callbackTimeframe': 'Do you need a call back?',
        'contactNumber': 'What is the best number to contact you?'
      },
      'IM Consult': {
        'patientDemographics': 'Patient demographics (age, sex, species, breed)',
        'currentMedicationDoses': 'Current medication doses, frequency',
        'currentDiet': 'Current diet (specify dry or canned)',
        'currentPatientHistory': 'Current patient history (including travel history if applicable)',
        'additionalPatientInfo': 'Additional patient information (exam findings, diagnostics performed)',
        'specificQuestionsGoals': 'Specific Questions/Goals of Consultation'
      },
      'IM Recheck': {
        'updatedWeightKg': 'Updated weight (kg)',
        'updatedHistory': 'Updated History',
        'updatedMedications': 'Updated Medications (doses, frequency)',
        'updatedDiet': 'Updated Diet (if new/changed)',
        'additionalPatientInfo': 'Additional patient information (exam findings, diagnostics performed)',
        'newConditionsDiagnosed': 'Have any new conditions been diagnosed?',
        'specificQuestionsGoals': 'Specific questions/goals of consultation'
      },
      'Initial Ophthalmology': {
        'currentMedications': 'Current medications (topical and systemic)',
        'currentDiagnosisTreatment': 'Current diagnosis, treatment',
        'currentConcerns': 'Current concerns',
        'diagnosticsApplicable': 'Diagnostics (if applicable)',
        'schirmerTearTestOD': 'Schirmer Tear Test OD (mm/min)',
        'schirmerTearTestOS': 'Schirmer Tear Test OS (mm/min)',
        'iopOD': 'IOP OD (mmHg)',
        'iopOS': 'IOP OS (mmHg)',
        'tonometerType': 'Type of tonometer used',
        'fluoresceinStainOD': 'Fluorescein Stain OD',
        'fluoresceinStainOS': 'Fluorescein Stain OS',
        'specificQuestionsGoals': 'Specific questions/goals of consultation',
        'priority': 'Priority Level'
      },
      'Recheck Ophthalmology': {
        'updatedWeightKg': 'Updated weight in kg',
        'currentMedications': 'Current medications (topical and systemic)',
        'currentDiagnosisTreatments': 'Current diagnosis, treatments',
        'changesInCase': 'Any changes in case, response to previous therapy',
        'currentConcerns': 'Current concerns',
        'diagnosticsApplicable': 'Diagnostics (if applicable)',
        'schirmerTearTestOD': 'Schirmer Tear Test OD (mm/min)',
        'schirmerTearTestOS': 'Schirmer Tear Test OS (mm/min)',
        'iopOD': 'IOP OD (mmHg)',
        'iopOS': 'IOP OS (mmHg)',
        'tonometerType': 'Type of tonometer used',
        'fluoresceinStainOD': 'Fluorescein Stain OD',
        'fluoresceinStainOS': 'Fluorescein Stain OS',
        'specificQuestionsGoals': 'Specific questions/goals of recheck consult',
        'priority': 'Priority Level'
      },
      'Dermatology': {
        'patientColor': 'Color (please indicate if dilute color)',
        'problemDescription': 'Briefly describe the patient\'s problem',
        'currentProblemStart': 'When did the current problem begin?',
        'firstIssueAge': 'How old was the patient when the FIRST ear or skin issue(s) began?',
        'pruritusScore': 'Pruritus Score (1-10)',
        'pruritusPattern': 'Pruritus Pattern',
        'pruritusFocus': 'Where is the focus of the pruritus?',
        'patientEnvironment': 'Patient Environment',
        'currentMedicationsDiet': 'Current medications and diet',
        'parasitePrevention': 'Parasite prevention and frequency given',
        'priorMedications': 'Prior relevant medications',
        'eliminationDietTrial': 'Elimination diet trial',
        'advancedDiagnostics': 'Advanced diagnostic tests',
        'cytologyResults': 'Cytology, skin scrapes, or dermatophyte testing results',
        'dermatologicExamSummary': 'Dermatologic exam summary',
        'earConcerns': 'Ear concerns',
        'systemicSigns': 'Systemic signs',
        'specificQuestionsGoals': 'Specific questions/goal of consultation'
      },
      'Recheck Dermatology': {
        'patientColor': 'Color (please indicate if dilute color)',
        'patientStatus': 'Is the patient improved, worse, or the same?',
        'pruritusScore': 'Pruritus Score (1-10)',
        'pruritusFocus': 'Where is the focus of the pruritus?',
        'currentMedicationsDiet': 'Current medications and diet',
        'parasitePrevention': 'Parasite prevention and frequency given',
        'medicationResponse': 'Response to medications from previous consultation',
        'advancedDiagnostics': 'Advanced diagnostic tests performed since initial consultation',
        'cytologyResults': 'Cytology, skin scrapes, or dermatophyte testing results',
        'dermatologicExamSummary': 'Dermatologic exam summary',
        'earConcerns': 'Ear concerns',
        'hairCoatChanges': 'Hair coat changes',
        'systemicSigns': 'Systemic signs',
        'specificQuestionsGoals': 'Specific questions/goal of consultation'
      }
    };

    // Anaesthesia (pilot) forms share their labels with the backend via anaesthesiaIntake.ts
    if (specialtyType && ANAESTHESIA_FIELD_LABELS[specialtyType]?.[fieldName]) {
      return ANAESTHESIA_FIELD_LABELS[specialtyType][fieldName];
    }

    // Try to get label from specialty-specific mapping first
    if (specialtyType && fieldLabelsBySpecialty[specialtyType]) {
      const label = fieldLabelsBySpecialty[specialtyType][fieldName];
      if (label) return label;
    }

    // Fallback to formatted field name
    return this.formatFieldName(fieldName);
  }

  /**
   * Fallback method to format field names into readable labels
   */
  formatFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim();
  }

  /**
   * Method to determine field type for display
   * Now specialty-aware to prevent conflicts between specialties
   */
  getFieldType(fieldName: string, specialtyType?: string): string {
    const arrayFieldsBySpecialty: { [specialty: string]: string[] } = {
      'Feline Behavior': [],
      'Canine Behavior': ['aggressionTriggers'],
      'Recheck Oncology': ['updatedDiagnostics'],
      'Initial Oncology': ['diagnosticsPerformed'],
      'Emergency and Critical Care': [],
      'IM Consult': [],
      'IM Recheck': [],
      'Initial Ophthalmology': [],
      'Recheck Ophthalmology': []
    };

    if (specialtyType && ANAESTHESIA_ARRAY_FIELDS[specialtyType]) {
      return ANAESTHESIA_ARRAY_FIELDS[specialtyType].includes(fieldName) ? 'array' : 'text';
    }

    // Check if field is an array type for this specialty
    if (specialtyType && arrayFieldsBySpecialty[specialtyType]) {
      return arrayFieldsBySpecialty[specialtyType].includes(fieldName) ? 'array' : 'text';
    }

    return 'text';
  }

  /**
   * Method to determine if field should be displayed as textarea
   * Automatically shows textarea for fields with maxLength >= 200 characters
   */
  isLongTextField(fieldName: string, specialtyType?: string): boolean {
    // Automatically determine based on field's max length
    const maxLength = this.getFieldMaxLength(fieldName, specialtyType);

    // Show as textarea if max length is 200 or more characters
    return maxLength >= 500;
  }

  /**
   * Method to get maximum length for fields
   * Now specialty-aware to prevent conflicts between specialties
   */
  getFieldMaxLength(fieldName: string, specialtyType?: string): number {
    if (fieldName === 'prefersPhoneCall') return 3;
    if (fieldName === 'preferredPhoneNumber') return 30;

    // For Behavior specialty, we need to check both Canine and Feline Behavior limits
    // since the specialtyType is stored as just "Behavior"
    const isGenericBehavior = specialtyType === 'Behavior' || specialtyType === 'Behaviour';

    const fieldLimitsBySpecialty: { [specialty: string]: { [field: string]: number } } = {
      'Feline Behavior': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        'otherDvm': 100,
        // Initial consultation fields
        'presentingComplaint': 1000,
        'currentDiagnoses': 1000,
        'physicalExamFindings': 1000,
        'diagnosticsPerformed': 1000,
        'allMedications': 1000,
        'consultGoals': 1000,
        'additionalRelevantInfo': 1000,
        // Recheck fields
        'recheckCurrentBehaviorMedicalConditions': 1500,
        'recheckCurrentMedicationsResponse': 1500,
        'recheckGeneralProgressUpdate': 1500,
        'recheckBehaviorManagementModification': 1500,
        'recheckSpecificGoalsQuestions': 1500
      },
      'Canine Behavior': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        'otherDvm': 100,
        // Initial consultation fields
        'presentingComplaint': 1000,
        'currentDiagnoses': 1000,
        'physicalExamFindings': 1000,
        'diagnosticsPerformed': 1000,
        'allMedications': 1000,
        'consultGoals': 1000,
        'additionalRelevantInfo': 1000,
        // Recheck fields
        'recheckCurrentBehaviorMedicalConditions': 1500,
        'recheckCurrentMedicationsResponse': 1500,
        'recheckGeneralProgressUpdate': 1500,
        'recheckBehaviorManagementModification': 1500,
        'recheckSpecificGoalsQuestions': 1500,
        // Short fields
        'bitingIncidents': 200,
        'aggressionOtherSpecify': 200
      },
      'Recheck Oncology': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        // Administrative fields
        'requesterEmail': 100,
        'referringDoctorEmail': 100,
        'referringHospitalEmail': 100,
        'referringHospitalPhone': 20,
        'referringHospitalId': 50,
        'clientId': 50,
        'petId': 50,
        // Weight fields
        'updatedWeight': 50,
        'updatedWeightUnit': 10,
        // Clinical fields (no explicit maxLength in form, using conservative defaults ≤1500)
        'clinicalHistorySummary': 1500,
        'updatedDiagnostics': 1000,
        'otherPertinentHistory': 1500,
        'currentMedicationsTreatments': 1500,
        'responseToPreviousMedications': 1000,
        'additionalDiagnostics': 1500,
        'specificQuestionsGoals': 1500,
        // Other fields
        'patientQualityOfLife': 50,
        'newChemotherapyProtocol': 100
      },
      'Initial Oncology': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        // Administrative fields
        'requesterEmail': 100,
        'referringDoctorEmail': 100,
        'referringHospitalEmail': 100,
        'referringHospitalPhone': 20,
        'referringHospitalId': 50,
        'petOwnerFirstName': 100,
        'petOwnerLastName': 100,
        'clientId': 50,
        'petId': 50,
        'dateOfBirthAge': 50,
        'breed': 100,
        // Clinical fields (no explicit maxLength in form, using conservative defaults ≤1500)
        'patientDiagnosis': 1000,
        'clinicalHistorySummary': 1500,
        'diagnosticsPerformed': 1000,
        'previousCancers': 200,
        'currentMedicationsTreatments': 1500,
        'responseToPreviousMedications': 1000,
        'surgeryPerformed': 1000,
        'additionalDiagnostics': 1500,
        'specificQuestionsGoals': 1500,
        'medicalRecordsAttached': 500,
        // Other fields
        'patientQualityOfLife': 50,
        'chemotherapyProtocolRequested': 100
      },
      'Emergency and Critical Care': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        // Clinical fields (no explicit maxLength in form, using conservative defaults ≤1500)
        'currentDiet': 1000,
        'currentMedications': 1500,
        'diagnosticsPerformed': 1500,
        'consultationGoals': 1500,
        // Other fields
        'priority': 50,
        'callbackTimeframe': 100,
        'contactNumber': 14
      },
      'IM Consult': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        // Clinical fields (no explicit maxLength in form, keeping original generous limits)
        'patientDemographics': 500,
        'currentMedicationDoses': 1500,
        'currentDiet': 1000,
        'currentPatientHistory': 2000,
        'additionalPatientInfo': 2000,
        'specificQuestionsGoals': 1500
      },
      'IM Recheck': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        // Weight field
        'updatedWeightKg': 50,
        // Clinical fields (no explicit maxLength in form, keeping original generous limits)
        'updatedHistory': 2000,
        'updatedMedications': 1500,
        'updatedDiet': 1000,
        'additionalPatientInfo': 2000,
        'specificQuestionsGoals': 1500,
        'newConditionsDiagnosed': 200
      },
      'Initial Ophthalmology': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        'breed': 100,
        // Age/weight fields
        'patientAge': 50,
        // Clinical fields (no explicit maxLength in form, using conservative defaults ≤1500)
        'currentMedications': 1500,
        'currentDiagnosisTreatment': 1500,
        'currentConcerns': 1500,
        'diagnosticsApplicable': 1500,
        'specificQuestionsGoals': 1500,
        // Measurement fields
        'schirmerTearTestOD': 50,
        'schirmerTearTestOS': 50,
        'iopOD': 50,
        'iopOS': 50,
        'tonometerType': 100,
        'fluoresceinStainOD': 200,
        'fluoresceinStainOS': 200,
        // Other fields
        'priority': 50
      },
      'Recheck Ophthalmology': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        // Weight field
        'updatedWeightKg': 50,
        // Clinical fields (no explicit maxLength in form, using conservative defaults ≤1500)
        'currentMedications': 1500,
        'currentDiagnosisTreatments': 1500,
        'changesInCase': 1500,
        'currentConcerns': 1500,
        'diagnosticsApplicable': 1500,
        'specificQuestionsGoals': 1500,
        // Measurement fields
        'schirmerTearTestOD': 50,
        'schirmerTearTestOS': 50,
        'iopOD': 50,
        'iopOS': 50,
        'tonometerType': 100,
        'fluoresceinStainOD': 200,
        'fluoresceinStainOS': 200,
        // Other fields
        'priority': 50
      },
      'Dermatology': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        // Color and demographics
        'patientColor': 100,
        'firstIssueAge': 50,
        // Problem description fields (no explicit maxLength in form, using conservative defaults ≤1500)
        'problemDescription': 1500,
        'currentProblemStart': 300,
        'pruritusFocus': 500,
        'currentMedicationsDiet': 1500,
        'parasitePrevention': 300,
        'priorMedications': 1500,
        'eliminationDietTrial': 1500,
        'advancedDiagnostics': 1500,
        'cytologyResults': 1500,
        'dermatologicExamSummary': 1500,
        'earConcerns': 1500,
        'systemicSigns': 1500,
        'specificQuestionsGoals': 1500,
        'hairCoatChanges': 1000,
        // Score/pattern fields
        'pruritusScore': 10,
        'pruritusPattern': 100,
        'patientEnvironment': 100
      },
      'Recheck Dermatology': {
        // Base fields
        'ownerName': 100,
        'petName': 50,
        'petBreed': 50,
        // Color and status
        'patientColor': 100,
        'patientStatus': 100,
        // Clinical fields (no explicit maxLength in form, using conservative defaults ≤1500)
        'pruritusFocus': 500,
        'currentMedicationsDiet': 1500,
        'parasitePrevention': 300,
        'medicationResponse': 1500,
        'advancedDiagnostics': 1500,
        'cytologyResults': 1500,
        'dermatologicExamSummary': 1500,
        'earConcerns': 1500,
        'hairCoatChanges': 1500,
        'systemicSigns': 1500,
        'specificQuestionsGoals': 1500,
        // Score field
        'pruritusScore': 10
      }
    };

    const anaesthesiaLimit = specialtyType ? ANAESTHESIA_FIELD_MAX_LENGTHS[specialtyType]?.[fieldName] : undefined;
    if (anaesthesiaLimit) return anaesthesiaLimit;

    // Check if there's a specific limit for this field in this specialty
    if (specialtyType && fieldLimitsBySpecialty[specialtyType]) {
      const limit = fieldLimitsBySpecialty[specialtyType][fieldName];
      if (limit) return limit;
    }

    // For generic "Behavior" specialty, check both Canine and Feline Behavior limits
    // and return the maximum of the two (they should be the same)
    if (isGenericBehavior) {
      const canineLimit = fieldLimitsBySpecialty['Canine Behavior']?.[fieldName];
      const felineLimit = fieldLimitsBySpecialty['Feline Behavior']?.[fieldName];

      if (canineLimit || felineLimit) {
        return Math.max(canineLimit || 0, felineLimit || 0);
      }
    }

    // Default max length for most fields
    return 500;
  }

  /**
   * Method to format array values for display
   */
  formatArrayValue(value: any): string {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value || '';
  }

  /**
   * Generic method to get specialty type from appointment details
   */
  getSpecialtyType(appointmentDetails: any): string {
    return appointmentDetails?.specialtyIntakeData?.specialtyType || appointmentDetails?.speciality || '';
  }
}
